import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { internalAlphaAlpha001Api, type Alpha001Status, type SprintDashboard, type SprintValidation } from '../../api/internal-alpha-alpha001';

type Tab = 'overview' | 'modules' | 'dimensions' | 'objectives' | 'providers' | 'jobs' | 'gates' | 'status';

export function InternalAlphaAlpha001Page() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SprintDashboard | null>(null);
  const [modules, setModules] = useState<{ key: string; name: string; category: string }[]>([]);
  const [dimensions, setDimensions] = useState<{ key: string; name: string; category: string }[]>([]);
  const [objectives, setObjectives] = useState<{ key: string; name: string; category: string }[]>([]);
  const [providers, setProviders] = useState<{ key: string; name: string; interface: string }[]>([]);
  const [jobs, setJobs] = useState<{ key: string; name: string; schedule?: string }[]>([]);
  const [gates, setGates] = useState<{ key: string; name: string; blocking: boolean }[]>([]);
  const [status, setStatus] = useState<Alpha001Status | null>(null);
  const [validation, setValidation] = useState<SprintValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const card = 'border-amber-200 bg-white dark:border-amber-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-amber-700 dark:text-amber-300';

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, mod, dims, obj, prov, jb, gt, st] = await Promise.all([
        internalAlphaAlpha001Api.dashboard(), internalAlphaAlpha001Api.operatingModules(),
        internalAlphaAlpha001Api.operatingDimensions(), internalAlphaAlpha001Api.objectives(),
        internalAlphaAlpha001Api.providers(), internalAlphaAlpha001Api.backgroundJobs(),
        internalAlphaAlpha001Api.qualityGates(), internalAlphaAlpha001Api.status(),
      ]);
      setDashboard(dash); setModules(mod); setDimensions(dims); setObjectives(obj);
      setProviders(prov); setJobs(jb); setGates(gt); setStatus(st);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load sprint center'); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await internalAlphaAlpha001Api.validate({
        no_critical_defects: true, daily_ops_on_kartmanage: true,
        recommendations_trusted: true, customer_value_accurate: true,
        performance_targets_met: true, founder_daily_use: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Internal Alpha Bible passes all exit quality gates' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Validation failed'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'modules', label: 'Operating Modules' },
    { id: 'dimensions', label: 'Operating Dimensions' }, { id: 'objectives', label: 'Objectives' },
    { id: 'providers', label: 'Providers' }, { id: 'jobs', label: 'Jobs' },
    { id: 'gates', label: 'Exit Gates' }, { id: 'status', label: 'Status' },
  ];

  return (
    <div className="space-y-6 rounded-xl bg-amber-50/20 p-1 dark:bg-zinc-950">
      <div>
        <p className={`text-xs font-semibold uppercase ${accent}`}>ALPHA-000001 · P0 · Mandatory Before External Pilot</p>
        <h1 className="text-2xl font-semibold">Internal Alpha Operating & Validation Bible</h1>
        <p className={`text-sm ${muted}`}>Founder testing framework, daily operating procedures, product validation and continuous refinement using Nakabh real business data.</p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <Link to="/platform/screen-review-xdb002" className={`${accent} hover:underline`}>XDB-000002</Link>
          <span className={muted}>→</span>
          <Link to="/internal-alpha" className={`${accent} hover:underline`}>Live Founder Alpha Command Center</Link>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-amber-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
        <button type="button" onClick={validate} disabled={busy} className="rounded-lg bg-amber-700 px-3 py-1.5 text-sm text-white disabled:opacity-50">Validate Exit Criteria</button>
      </div>

      {tab === 'overview' && dashboard && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Objectives', value: dashboard.objectives },
            { label: 'Operating Modules', value: dashboard.operating_modules },
            { label: 'Validation Areas', value: dashboard.validation_areas },
            { label: 'Quality Gates', value: dashboard.quality_gates },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-2xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'modules' && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {modules.map((m) => (
            <div key={m.key} className={`rounded-xl border p-3 ${card}`}>
              <p className="font-medium">{m.name}</p>
              <p className={`text-xs capitalize ${muted}`}>{m.category}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'dimensions' && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {dimensions.map((d) => (
            <div key={d.key} className={`rounded-xl border p-3 ${card}`}>
              <p className="font-medium">{d.name}</p>
              <p className={`text-xs capitalize ${muted}`}>{d.category}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'objectives' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {objectives.map((o) => (
            <div key={o.key} className={`rounded-xl border p-3 ${card}`}>
              <p className="font-medium">{o.name}</p>
              <p className={`text-xs capitalize ${muted}`}>{o.category}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'providers' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {providers.map((p) => (
            <div key={p.key} className={`rounded-xl border p-3 ${card}`}>
              <p className="font-medium">{p.name}</p>
              <p className={`font-mono text-xs ${muted}`}>{p.interface}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'jobs' && (
        <div className="space-y-2">
          {jobs.map((j) => (
            <div key={j.key} className={`flex items-center justify-between rounded-xl border p-3 ${card}`}>
              <p className="font-medium">{j.name}</p>
              <p className={`font-mono text-xs ${muted}`}>{j.schedule}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'gates' && (
        <div className="space-y-2">
          {gates.map((g) => (
            <div key={g.key} className={`flex items-center justify-between rounded-xl border p-3 ${card}`}>
              <p className="font-medium">{g.name}</p>
              {g.blocking && <span className="text-xs font-semibold text-red-600">Blocking</span>}
            </div>
          ))}
          {validation && (
            <p className={`text-sm ${validation.valid ? 'text-emerald-600' : 'text-orange-600'}`}>
              Last validation: {validation.valid ? 'All exit gates passed' : `Gaps: ${validation.gaps?.join(', ')}`}
            </p>
          )}
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <pre className="overflow-auto text-xs">{JSON.stringify(status, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
